import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import api from '../../../../api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSliders, faTimes, faUser, faCalendarAlt, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../Layouts/context/Authcontext';
import * as XLSX from 'xlsx';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import Swal from 'sweetalert2';

function TATFilter({ selectedClientId, selectedtoDate, selectedFromDate, selectedScopeId }) {
  const { accessToken, client, scope } = useAuth();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]); // holds [startDate, endDate]
  const [startDate, endDate] = dateRange; // destructure for easy use
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);

  const isRtl = useSelector((state) => state.themeConfig.rtlClass) === 'rtl';

  const [clients, setClients] = useState([]);
  const [scopes, setScopes] = useState([]);

  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const CustomCalendarContainer = ({ className, children }) => {
    return (
      <div
        className={className}
        onMouseLeave={() => setIsCalendarOpen(false)} // 👈 closes on mouse leave
      >
        {children}
      </div>
    );
  };

  // Fetch Clients from API
  useEffect(() => {
    const fetchClients = async () => {
      setLoadingClients(true);
      try {
        let baseUrls = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrls}/client/getAllClient`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (response.data && Array.isArray(response.data)) {
          const clientOptions = response.data
            .filter((client_s) => client.includes(client_s._id))
            .map((client) => ({ label: client.client, value: client._id }));
          setClients(clientOptions);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, [accessToken, client]);

  // Fetch Scopes based on selected Client
  const fetchScopes = async (clientId) => {
    setLoadingScopes(true);
    setScopes([]); // Clear previous scopes
    try {
      let baseUrls = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/qaDashboard/getClientByScope?clientId=${clientId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.data && Array.isArray(response.data.data)) {
        const scopeOptions = response.data.data
          .filter((scopeItem) => scope.includes(scopeItem._id))
          .map((scopeItem) => ({ label: scopeItem.scope, value: scopeItem._id }));
        setScopes(scopeOptions);
      }
    } catch (error) {
      console.error('Error fetching scopes:', error);
      setScopes([]);
    } finally {
      setLoadingScopes(false);
    }
  };

  // const exportToExcel = async () => {
  //   event.preventDefault(); // Prevent form submission and page refresh

  //   if (!fromDate || !toDate || !selectedScope) {
  //     alert("Please select a valid date range and scope.");
  //     return;
  //   }

  //   const formattedFromDate = fromDate.toLocaleDateString('en-US');
  //   const formattedToDate = toDate.toLocaleDateString('en-US');
  //   const scopeIds = selectedScope.value;

  //   try {
  //     const response = await api.get(
  //       `${baseUrls}/managerDashboard/getCustomDateProductionRawData`,
  //       {
  //         params: { startDate: formattedFromDate, endDate: formattedToDate, scopeId: scopeIds },
  //         headers: { Authorization: `Bearer ${accessToken}` },
  //       }
  //     );

  //     if (response.data && Array.isArray(response.data.data)) {
  //       const worksheet = XLSX.utils.json_to_sheet(response.data.data);
  //       const workbook = XLSX.utils.book_new();
  //       XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
  //       XLSX.writeFile(workbook, "ProductionData.xlsx");
  //     } else {
  //       alert("No data available for the selected filters.");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching data for Excel export:", error);
  //     alert("Failed to download data. Please try again.");
  //   }
  // };

  function flattenObject(obj, parentKey = '', result = {}) {
    for (let key in obj) {
      if (!obj.hasOwnProperty(key)) continue;
      const newKey = parentKey ? `${parentKey}_${key}` : key;

      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        flattenObject(obj[key], newKey, result); // Recursive call for nested objects
      } else if (Array.isArray(obj[key])) {
        // Handle array of objects (like userAllocated)
        result[newKey] = obj[key].map((item) => (typeof item === 'object' ? Object.values(item).join(' ') : item)).join(', ');
      } else {
        result[newKey] = obj[key];
      }
    }
    return result;
  }

  const exportToExcel = async (event) => {
    event.preventDefault(); // Prevent form submission

    //  if (!startDate && !endDate && !selectedClient && !selectedScope) {
    //   alert("Please select a valid date range and scope.");
    //   return;
    // }

    let formattedFromDate, formattedToDate;

    // Function to format date in MM/DD/YYYY format
    const formatDate = (date) => {
      const month = date.getMonth() + 1; // Months are zero-based, so add 1
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month < 10 ? '0' + month : month}/${day < 10 ? '0' + day : day}/${year}`;
    };

    if (!startDate && !endDate) {
      const currentDate = new Date();
      const currentMonthStartDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      formattedFromDate = formatDate(currentMonthStartDate);
      formattedToDate = formatDate(currentDate);
    } else {
      formattedFromDate = formatDate(startDate);
      formattedToDate = formatDate(endDate);
    }

    const scopeIds = selectedScope ? selectedScope.value : '';
    const clientIds = selectedClient ? selectedClient.value : '';

    try {
      let baseUrls = getRoundRobinBaseUrl();
      const response = await api.get(`${baseUrls}/uploadedFile/getReport`, {
        params: {
          startDate: formattedFromDate,
          endDate: formattedToDate,
          scopeId: scopeIds,
          clientId: clientIds,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data && Array.isArray(response.data.getUploadedFile) && response.data.getUploadedFile.length > 0) {
        const mappedData = response.data.getUploadedFile.map((item) => flattenObject(item));

        const worksheet = XLSX.utils.json_to_sheet(mappedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'UploadedFiles');
        XLSX.writeFile(workbook, 'TATReport.xlsx');
      } else {
        Swal.fire({
          icon: 'error',
          title: 'No data found for the selected filters.',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error('Error fetching data for Excel export:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to download data. Please try again.',
        toast: true,
        position: 'top',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  return (
    <div className="relative">
      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute right-10 top-2 bg-[#808080] text-white text-xs py-1 px-3 rounded-md shadow-md">Open Filters</div>
      )}

      {/* Filter Icon Button with Pulse Effect */}
      <button
        className="absolute right-2 text-[#1A2B5C] text-2xl transition-all hover:text-[#EE1D23] z-50 animate-pulse"
        onClick={() => setIsFilterOpen(!isFilterOpen)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <FontAwesomeIcon icon={faSliders} />
      </button>

      {/* Filter Popup (Floating Bubble) */}
      {isFilterOpen && (
        <div className="absolute right-2 top-0 bg-[#1A2B5C] text-white border border-[#1A2B5C] rounded-xl shadow-2xl p-5 w-64 z-50 animate-slide-in backdrop-blur-md bg-opacity-90">
          {/* Close Button */}
          <button
            className="absolute top-2 right-2 text-white text-lg transition-all hover:text-[#EE1D23]"
            onClick={() => setIsFilterOpen(false)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          {/* Title */}
          <h2 className="text-lg font-semibold text-white text-center mb-4">Filter Options</h2>

          {/* Dropdowns */}
          <form className="space-y-3">
            {/* Client */}
            <div className="flex items-center space-x-2 bg-white bg-opacity-10 rounded-md p-2">
              <FontAwesomeIcon icon={faUser} className="text-white" />
              <Select
                options={clients}
                isLoading={loadingClients}
                placeholder="Select Client..."
                className="w-full text-sm bg-transparent text-black"
                value={selectedClient} // Set selected value
                onChange={(selected) => {
                  setSelectedClient(selected); // Update state
                  fetchScopes(selected.value);
                  selectedClientId(selected.value); // Update parent state
                  selectedScopeId(null);
                  setSelectedScope(null);
                }}
              />
            </div>

            {/* Scope */}
            <div className="flex items-center space-x-2 bg-white bg-opacity-10 rounded-md p-2">
              <FontAwesomeIcon icon={faClipboardList} className="text-white" />
              <Select
                options={scopes}
                isLoading={loadingScopes}
                placeholder="Select Scope..."
                className="w-full text-sm bg-transparent text-black"
                value={selectedScope} // Set selected value
                onChange={(selected) => {
                  setSelectedScope(selected); // Update state
                  selectedScopeId(selected.value);
                }}
              />
            </div>

            {/* Date Range Picker */}
            <div className="flex items-center space-x-2 bg-white bg-opacity-10 rounded-md p-2">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-white" />
              <DatePicker
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  setDateRange(update);

                  const [start, end] = update;

                  if (start) {
                    selectedFromDate(start);
                  }

                  if (end) {
                    selectedtoDate(end);
                  }
                }}
                isClearable
                placeholderText="Select date range"
                className="w-full p-2 bg-transparent text-white placeholder-white border-none"
                onCalendarOpen={() => setIsCalendarOpen(true)}
                onCalendarClose={() => setIsCalendarOpen(false)}
                open={isCalendarOpen}
                calendarContainer={CustomCalendarContainer}
                onClick={() => setIsCalendarOpen(true)}
                onInputClick={() => setIsCalendarOpen(true)} // <-- THIS triggers on input click
                onFocus={() => setIsCalendarOpen(true)}
              />
            </div>
            <button
              onClick={(event) => exportToExcel(event)} // Pass event to prevent default behavior
              className="w-full bg-[#EE1D23] hover:bg-[#1A2B5C] text-white font-bold py-2 px-4 rounded flex items-center justify-center mt-4"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" /> Export to Excel
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default TATFilter;
